/**
 * PSP/CRE TOOL v2.1 - EVACUATION PARADIGMS MODULE
 * 
 * @fileoverview Medical evacuation paradigms based on tactical situation
 * @module constants/evacuationParadigms
 * @author Dr. Tatar Radu MD - Romanian Military Medical Institute
 * @date December 22, 2024
 * @version 2.1 - icons.js Integration
 * 
 * DATA SOURCES:
 * - Ukraine 2022-2024 operational data (validated survival rates)
 * - NATO AJMedP-5 (Aeromedical Evacuation doctrine)
 * - US Army ATP 4-02.2 Medical Evacuation
 * - Dupuy Institute casualty research
 * - Israeli Defense Forces medical doctrine
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (STATUS_ICONS, TRANSPORT_ICONS, WEATHER_ICONS)
 * - Added getParadigmOptions() for UI dropdowns
 * - Added getDroneThreatOptions() for UI
 * - Added getWeatherOptions() for UI
 * - Added validateEvacuationConfig() function
 * - Added getParadigmIcon() helper
 * - Educational descriptions enhanced
 * 
 * CRITICAL SURVIVAL DATA (Ukraine 2022-2024 Validated):
 * - Golden Hour (<60 min): 92% T1 survival
 * - Platinum 10 (<10 min): 98% T1 survival (helicopter pickup)
 * - 60-120 min: 76% T1 survival
 * - 120-180 min: 54% T1 survival
 * - 180-240 min: 32% T1 survival
 * - >240 min: 18% T1 survival
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  TRANSPORT_ICONS,
  WEATHER_ICONS,
  TACTICAL_ICONS,
  MEDICAL_ICONS,
  TIME_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  BLUE: STATUS_ICONS?.BLUE || '🔵',
  
  // Transport icons
  HELICOPTER: TRANSPORT_ICONS?.HELICOPTER || '🚁',
  AMBULANCE: TRANSPORT_ICONS?.AMBULANCE || '🚑',
  VEHICLE: TRANSPORT_ICONS?.VEHICLE || '🚗',
  
  // Weather icons
  SUN: WEATHER_ICONS?.SUN || '☀️',
  CLOUD: WEATHER_ICONS?.CLOUD || '☁️',
  RAIN: WEATHER_ICONS?.RAIN || '🌧️',
  SNOW: WEATHER_ICONS?.SNOW || '❄️',
  FOG: WEATHER_ICONS?.FOG || '🌫️',
  STORM: WEATHER_ICONS?.STORM || '⛈️',
  
  // Tactical icons
  DRONE: TACTICAL_ICONS?.DRONE || '🛸',
  WARNING: TACTICAL_ICONS?.WARNING || '⚠️',
  SHIELD: TACTICAL_ICONS?.SHIELD || '🛡️',
  TARGET: TACTICAL_ICONS?.TARGET || '🎯',
  
  // Medical icons
  MEDICAL: MEDICAL_ICONS?.CROSS || '➕',
  CLOCK: TIME_ICONS?.CLOCK || '⏱️',
  HOURGLASS: TIME_ICONS?.HOURGLASS || '⏳'
};

// ============================================================================
// TIME-BASED SURVIVAL RATES (Ukraine 2022-2024 Validated)
// ============================================================================

/**
 * T1 (Immediate) Casualty Survival by Evacuation Time
 * Based on Ukraine 2022-2024 combat casualty data
 * These rates assume proper TCCC at point of injury
 */
export const T1_SURVIVAL_BY_TIME = {
  PLATINUM_10: {
    code: 'PLATINUM_10',
    range: '0-10 minutes',
    max_minutes: 10,
    survival_rate: 0.98,
    icon: ICONS.GREEN,
    description: 'Platinum 10 minutes - immediate helicopter pickup',
    achievable_in: ['PERMISSIVE'],
    notes: 'Requires pre-positioned MEDEVAC, rare in combat',
    educational: {
      key_point: 'Best possible outcome - 98% survival',
      teaching_note: 'Only achievable with air superiority and pre-positioned assets'
    }
  },
  GOLDEN_HOUR: {
    code: 'GOLDEN_HOUR',
    range: '10-60 minutes',
    max_minutes: 60,
    survival_rate: 0.92,
    icon: ICONS.GREEN,
    description: 'Golden Hour - surgery within 60 minutes',
    achievable_in: ['PERMISSIVE', 'CONTESTED'],
    notes: 'Standard NATO doctrine goal',
    educational: {
      key_point: 'NATO standard - 92% survival',
      teaching_note: 'This is what we plan for - achievable in 38% of Ukraine cases'
    }
  },
  EXTENDED_1: {
    code: 'EXTENDED_1',
    range: '60-120 minutes',
    max_minutes: 120,
    survival_rate: 0.76,
    icon: ICONS.YELLOW,
    description: 'Extended evacuation - 1-2 hours',
    achievable_in: ['PERMISSIVE', 'CONTESTED', 'DENIED'],
    notes: 'Common in contested environment',
    pfc_required: true,
    educational: {
      key_point: 'PFC becomes critical - 16% survival drop',
      teaching_note: 'Most common scenario in Ukraine 2022-2024'
    }
  },
  EXTENDED_2: {
    code: 'EXTENDED_2',
    range: '120-180 minutes',
    max_minutes: 180,
    survival_rate: 0.54,
    icon: ICONS.ORANGE,
    description: 'Prolonged evacuation - 2-3 hours',
    achievable_in: ['CONTESTED', 'DENIED'],
    notes: 'PFC required, survival degrading',
    pfc_required: true,
    pfc_critical: true,
    educational: {
      key_point: 'Critical threshold - nearly half die',
      teaching_note: 'Without PFC, mortality approaches 70%'
    }
  },
  EXTENDED_3: {
    code: 'EXTENDED_3',
    range: '180-240 minutes',
    max_minutes: 240,
    survival_rate: 0.32,
    icon: ICONS.RED,
    description: 'Critical delay - 3-4 hours',
    achievable_in: ['DENIED'],
    notes: 'PFC critical, many expectant',
    pfc_required: true,
    pfc_critical: true,
    educational: {
      key_point: 'Two-thirds will die',
      teaching_note: 'Consider expectant triage for most severe injuries'
    }
  },
  BEYOND_4H: {
    code: 'BEYOND_4H',
    range: '>240 minutes',
    max_minutes: Infinity,
    survival_rate: 0.18,
    icon: ICONS.RED,
    description: 'Catastrophic delay - >4 hours',
    achievable_in: ['DENIED'],
    notes: 'Medical system failure, mass expectant',
    pfc_required: true,
    dcs_required: true,
    educational: {
      key_point: 'Medical system has failed',
      teaching_note: 'Only 1 in 5 T1 casualties survive - triage to expectant'
    }
  }
};

/**
 * T2 (Delayed) Casualty Survival by Evacuation Time
 * More forgiving timeline but still time-sensitive
 */
export const T2_SURVIVAL_BY_TIME = {
  UNDER_2H: {
    code: 'UNDER_2H',
    range: '0-120 minutes',
    max_minutes: 120,
    survival_rate: 0.96,
    icon: ICONS.GREEN,
    description: 'Optimal T2 evacuation'
  },
  UNDER_4H: {
    code: 'UNDER_4H',
    range: '120-240 minutes',
    max_minutes: 240,
    survival_rate: 0.89,
    icon: ICONS.GREEN,
    description: 'Acceptable T2 evacuation'
  },
  UNDER_6H: {
    code: 'UNDER_6H',
    range: '240-360 minutes',
    max_minutes: 360,
    survival_rate: 0.78,
    icon: ICONS.YELLOW,
    description: 'Extended T2 evacuation - PFC required'
  },
  UNDER_12H: {
    code: 'UNDER_12H',
    range: '360-720 minutes',
    max_minutes: 720,
    survival_rate: 0.62,
    icon: ICONS.ORANGE,
    description: 'Prolonged T2 - significant degradation'
  },
  BEYOND_12H: {
    code: 'BEYOND_12H',
    range: '>720 minutes',
    max_minutes: Infinity,
    survival_rate: 0.41,
    icon: ICONS.RED,
    description: 'Critical T2 delay'
  }
};

// ============================================================================
// EVACUATION PARADIGM DEFINITIONS
// ============================================================================

/**
 * PERMISSIVE ENVIRONMENT - Air Superiority, Forward Surgical Teams Safe
 */
export const PERMISSIVE = {
  value: 'PERMISSIVE',
  label: 'Permissive Environment',
  icon: ICONS.GREEN,
  color: '#4CAF50',
  description: 'Air superiority, forward surgical teams safe, optimal evacuation',
  
  // Core rates (Ukraine 2022-2024 validated)
  golden_hour_achievable_rate: 0.75,
  helicopter_availability: 0.85,
  ground_evacuation_rate: 0.15,
  pfc_required_rate: 0.10,
  median_evacuation_time_minutes: 45,
  t1_survival_rate: 0.92,
  
  // Detailed time distribution
  evacuation_time_distribution: {
    under_30min: 0.35,
    '30_to_60min': 0.40,
    '60_to_120min': 0.18,
    '120_to_180min': 0.05,
    over_180min: 0.02
  },
  
  // Recommended MTF positioning
  recommended_role2_distance: {
    min_km: 15,
    optimal_km: 20,
    max_km: 30,
    rationale: 'Close positioning safe, maximize golden hour'
  },
  
  // Educational content
  educational: {
    scenario: 'NATO peace enforcement, limited opposition',
    historical_example: 'Early OEF 2001-2003',
    key_teaching_point: 'This is the environment we train for - MEDEVAC dominant',
    student_exercise: 'Calculate helicopter sortie requirements for brigade'
  },
  
  // Ukraine applicability
  ukraine_data: {
    applicable: true,
    occurrence_rate: 0.15,
    notes: 'Rear areas only, brief windows during offensive'
  }
};

/**
 * CONTESTED ENVIRONMENT - Air Parity, Increased Ground Evacuation
 */
export const CONTESTED = {
  value: 'CONTESTED',
  label: 'Contested Environment',
  icon: ICONS.YELLOW,
  color: '#FFC107',
  description: 'Air parity, helicopter use limited, PFC capabilities required',
  
  // Core rates
  golden_hour_achievable_rate: 0.25,
  helicopter_availability: 0.40,
  ground_evacuation_rate: 0.50,
  pfc_required_rate: 0.55,
  median_evacuation_time_minutes: 95,
  t1_survival_rate: 0.76,
  
  // Detailed time distribution
  evacuation_time_distribution: {
    under_30min: 0.08,
    '30_to_60min': 0.17,
    '60_to_120min': 0.35,
    '120_to_180min': 0.28,
    over_180min: 0.12
  },
  
  // Recommended MTF positioning
  recommended_role2_distance: {
    min_km: 20,
    optimal_km: 30,
    max_km: 45,
    rationale: 'Balance golden hour vs MTF survivability'
  },
  
  // Educational content
  educational: {
    scenario: 'Peer conflict, contested airspace',
    historical_example: 'Ukraine 2022-2024 typical',
    key_teaching_point: 'PFC skills essential - most evacuations exceed golden hour',
    student_exercise: 'Plan ground ambulance routes considering drone threat'
  },
  
  // Ukraine applicability
  ukraine_data: {
    applicable: true,
    occurrence_rate: 0.60,
    notes: 'Most common paradigm in Ukraine conflict'
  }
};

/**
 * DENIED ENVIRONMENT - Air Superiority Lost, Ground Only
 */
export const DENIED = {
  value: 'DENIED',
  label: 'Denied Environment',
  icon: ICONS.RED,
  color: '#F44336',
  description: 'Enemy air superiority, MEDEVAC grounded, extended PFC required',
  
  // Core rates
  golden_hour_achievable_rate: 0.05,
  helicopter_availability: 0.10,
  ground_evacuation_rate: 0.70,
  pfc_required_rate: 0.85,
  median_evacuation_time_minutes: 180,
  t1_survival_rate: 0.54,
  
  // Detailed time distribution
  evacuation_time_distribution: {
    under_30min: 0.02,
    '30_to_60min': 0.03,
    '60_to_120min': 0.15,
    '120_to_180min': 0.35,
    over_180min: 0.45
  },
  
  // Recommended MTF positioning
  recommended_role2_distance: {
    min_km: 35,
    optimal_km: 50,
    max_km: 75,
    rationale: 'MTF survival priority over speed'
  },
  
  // Educational content
  educational: {
    scenario: 'Encirclement, enemy air dominance',
    historical_example: 'Mariupol 2022, some Bakhmut sectors',
    key_teaching_point: 'Forward surgical capability essential - expect mass casualties',
    student_exercise: 'Triage exercise with 45% T1 expectant conversion'
  },
  
  // Ukraine applicability
  ukraine_data: {
    applicable: true,
    occurrence_rate: 0.25,
    notes: 'Forward positions, encircled units, heavy drone presence'
  }
};

/**
 * Combined evacuation paradigms object
 */
export const EVACUATION_PARADIGMS = {
  PERMISSIVE,
  CONTESTED,
  DENIED
};

export const PARADIGM_VALUES = ['PERMISSIVE', 'CONTESTED', 'DENIED'];

// CALC-005 (2026-10-09): single place that reads the evacuation paradigm from a scenario.
// Tab 2 owns operation_parameters.evacuation_paradigm (APP_RULES Rule 15) and always writes it.
// Only scenarios saved before the field existed lack it; they get the documented default
// (CONTESTED = most common Ukraine 2022-2024 condition, see CONTESTED.ukraine_data) and the
// result says so via `defaulted`, so the caller can surface it. A present but unknown value
// is a data error and throws (APP_RULES Rule 6.1).
export const DEFAULT_EVACUATION_PARADIGM = 'CONTESTED';

export const resolveEvacuationParadigm = (scenario) => {
  const value = scenario?.operation_parameters?.evacuation_paradigm;
  if (value === undefined || value === null || value === '') {
    return { paradigm: DEFAULT_EVACUATION_PARADIGM, defaulted: true };
  }
  if (!PARADIGM_VALUES.includes(value)) {
    throw new Error(
      `resolveEvacuationParadigm: unknown evacuation_paradigm '${value}' ` +
      `(valid: ${PARADIGM_VALUES.join(', ')}). Path: scenario.operation_parameters.evacuation_paradigm (set in Tab 2)`
    );
  }
  return { paradigm: value, defaulted: false };
};

// ============================================================================
// DRONE THREAT MODIFIERS (Ukraine 2022-2024 Critical)
// ============================================================================

/**
 * Drone threat dramatically impacts evacuation operations
 * Ukraine 2022-2024: 18% of casualties from drone strikes
 * FPV drones specifically target medical vehicles and helicopters
 */
export const DRONE_THREAT_EVACUATION_MODIFIERS = {
  NONE: {
    code: 'NONE',
    label: 'No Drone Threat',
    icon: ICONS.GREEN,
    description: 'No drone threat',
    helicopter_modifier: 1.0,
    ground_speed_modifier: 1.0,
    route_distance_modifier: 1.0,
    movement_restriction: 'NONE',
    daylight_operations: true,
    recommended_paradigm_shift: null
  },
  LOW: {
    code: 'LOW',
    label: 'Low Drone Threat',
    icon: ICONS.GREEN,
    description: 'Occasional reconnaissance drones',
    helicopter_modifier: 0.95,
    ground_speed_modifier: 0.95,
    route_distance_modifier: 1.1,
    movement_restriction: 'CAUTION',
    daylight_operations: true,
    recommended_paradigm_shift: null
  },
  MEDIUM: {
    code: 'MEDIUM',
    label: 'Medium Drone Threat',
    icon: ICONS.YELLOW,
    description: 'Regular drone surveillance, occasional FPV attacks',
    helicopter_modifier: 0.75,
    ground_speed_modifier: 0.80,
    route_distance_modifier: 1.3,
    movement_restriction: 'CONCEALMENT',
    daylight_operations: true,
    recommended_paradigm_shift: null,
    notes: 'Use terrain masking, dispersed movement',
    educational: {
      teaching_note: 'Standard Ukraine 2023-2024 conditions'
    }
  },
  HIGH: {
    code: 'HIGH',
    label: 'High Drone Threat',
    icon: ICONS.ORANGE,
    description: 'Persistent drone threat, frequent FPV attacks',
    helicopter_modifier: 0.40,
    ground_speed_modifier: 0.60,
    route_distance_modifier: 1.5,
    movement_restriction: 'LIMITED_WINDOWS',
    daylight_operations: false,
    recommended_paradigm_shift: 'CONTESTED',
    notes: 'Consider night movement, EW support required',
    educational: {
      teaching_note: 'Forward positions in Ukraine - MEDEVAC near impossible'
    }
  },
  EXTREME: {
    code: 'EXTREME',
    label: 'Extreme Drone Threat',
    icon: ICONS.RED,
    description: 'Continuous drone surveillance, immediate attack response',
    helicopter_modifier: 0.15,
    ground_speed_modifier: 0.40,
    route_distance_modifier: 2.0,
    movement_restriction: 'NIGHT_ONLY',
    daylight_operations: false,
    recommended_paradigm_shift: 'DENIED',
    notes: 'Daytime movement extremely dangerous',
    educational: {
      teaching_note: 'Bakhmut 2023 conditions - any visible movement targeted within minutes'
    }
  }
};

// ============================================================================
// WEATHER EVACUATION MODIFIERS
// ============================================================================

export const WEATHER_EVACUATION_MODIFIERS = {
  CLEAR: {
    code: 'CLEAR',
    label: 'Clear Weather',
    icon: ICONS.SUN,
    helicopter_availability: 1.0,
    ground_speed_modifier: 1.0,
    drone_threat_modifier: 1.0,
    visibility: 'GOOD'
  },
  PARTLY_CLOUDY: {
    code: 'PARTLY_CLOUDY',
    label: 'Partly Cloudy',
    icon: ICONS.CLOUD,
    helicopter_availability: 0.95,
    ground_speed_modifier: 1.0,
    drone_threat_modifier: 0.95,
    visibility: 'GOOD'
  },
  OVERCAST: {
    code: 'OVERCAST',
    label: 'Overcast',
    icon: ICONS.CLOUD,
    helicopter_availability: 0.90,
    ground_speed_modifier: 1.0,
    drone_threat_modifier: 0.80,
    visibility: 'MODERATE'
  },
  RAIN_LIGHT: {
    code: 'RAIN_LIGHT',
    label: 'Light Rain',
    icon: ICONS.RAIN,
    helicopter_availability: 0.75,
    ground_speed_modifier: 0.85,
    drone_threat_modifier: 0.60,
    visibility: 'REDUCED',
    educational: {
      teaching_note: 'Light rain reduces drone effectiveness significantly'
    }
  },
  RAIN_HEAVY: {
    code: 'RAIN_HEAVY',
    label: 'Heavy Rain',
    icon: ICONS.STORM,
    helicopter_availability: 0.40,
    ground_speed_modifier: 0.65,
    drone_threat_modifier: 0.30,
    visibility: 'POOR'
  },
  FOG: {
    code: 'FOG',
    label: 'Fog',
    icon: ICONS.FOG,
    helicopter_availability: 0.20,
    ground_speed_modifier: 0.50,
    drone_threat_modifier: 0.20,
    visibility: 'VERY_POOR'
  },
  SNOW_LIGHT: {
    code: 'SNOW_LIGHT',
    label: 'Light Snow',
    icon: ICONS.SNOW,
    helicopter_availability: 0.60,
    ground_speed_modifier: 0.70,
    drone_threat_modifier: 0.50,
    visibility: 'REDUCED'
  },
  SNOW_HEAVY: {
    code: 'SNOW_HEAVY',
    label: 'Heavy Snow',
    icon: ICONS.SNOW,
    helicopter_availability: 0.15,
    ground_speed_modifier: 0.35,
    drone_threat_modifier: 0.10,
    visibility: 'VERY_POOR'
  },
  DUST_STORM: {
    code: 'DUST_STORM',
    label: 'Dust Storm',
    icon: ICONS.STORM,
    helicopter_availability: 0.10,
    ground_speed_modifier: 0.40,
    drone_threat_modifier: 0.05,
    visibility: 'NONE'
  }
};

// ============================================================================
// AIR DEFENSE THREAT MODIFIERS
// ============================================================================

export const AD_THREAT_EVACUATION_MODIFIERS = {
  NONE: {
    code: 'NONE',
    label: 'No AD Threat',
    icon: ICONS.GREEN,
    description: 'No AD threat - air superiority',
    helicopter_modifier: 1.0,
    flight_profile: 'NORMAL',
    paradigm_tendency: 'PERMISSIVE'
  },
  MANPADS_ONLY: {
    code: 'MANPADS_ONLY',
    label: 'MANPADS Only',
    icon: ICONS.YELLOW,
    description: 'MANPADS threat only',
    helicopter_modifier: 0.80,
    flight_profile: 'LOW_LEVEL',
    paradigm_tendency: 'PERMISSIVE',
    countermeasures_required: true
  },
  SHORAD: {
    code: 'SHORAD',
    label: 'Short Range AD',
    icon: ICONS.ORANGE,
    description: 'Short-range AD systems present',
    helicopter_modifier: 0.55,
    flight_profile: 'NOE',
    paradigm_tendency: 'CONTESTED',
    countermeasures_required: true
  },
  MEDIUM_SAM: {
    code: 'MEDIUM_SAM',
    label: 'Medium SAM',
    icon: ICONS.RED,
    description: 'Medium-range SAM threat',
    helicopter_modifier: 0.30,
    flight_profile: 'MASKED',
    paradigm_tendency: 'CONTESTED'
  },
  INTEGRATED_IADS: {
    code: 'INTEGRATED_IADS',
    label: 'Integrated IADS',
    icon: ICONS.RED,
    description: 'Integrated Air Defense System',
    helicopter_modifier: 0.10,
    flight_profile: 'EMERGENCY_ONLY',
    paradigm_tendency: 'DENIED'
  },
  DENIED: {
    code: 'DENIED',
    label: 'Air Denied',
    icon: ICONS.RED,
    description: 'Complete air denial',
    helicopter_modifier: 0.0,
    flight_profile: 'NO_FLIGHT',
    paradigm_tendency: 'DENIED'
  }
};

// ============================================================================
// NIGHT OPERATIONS MODIFIERS
// ============================================================================

export const NIGHT_OPERATIONS_MODIFIERS = {
  HELICOPTER: {
    NVG_EQUIPPED: {
      code: 'NVG_EQUIPPED',
      label: 'NVG Equipped Crew',
      icon: ICONS.GREEN,
      availability_modifier: 0.85,
      speed_modifier: 0.90,
      description: 'NVG-equipped crews - minor reduction'
    },
    FLIR_EQUIPPED: {
      code: 'FLIR_EQUIPPED',
      label: 'FLIR Equipped',
      icon: ICONS.GREEN,
      availability_modifier: 0.95,
      speed_modifier: 0.95,
      description: 'FLIR-equipped - near-normal operations'
    },
    NON_EQUIPPED: {
      code: 'NON_EQUIPPED',
      label: 'No Night Equipment',
      icon: ICONS.RED,
      availability_modifier: 0.30,
      speed_modifier: 0.60,
      description: 'Non-NVG crews - severely limited'
    }
  },
  GROUND: {
    NVG_EQUIPPED: {
      code: 'NVG_EQUIPPED',
      label: 'NVG Ground Crew',
      icon: ICONS.GREEN,
      speed_modifier: 0.75,
      accident_risk_increase: 0.15,
      description: 'NVG-equipped ground crews'
    },
    NON_EQUIPPED: {
      code: 'NON_EQUIPPED',
      label: 'No Night Equipment',
      icon: ICONS.RED,
      speed_modifier: 0.40,
      accident_risk_increase: 0.35,
      description: 'Non-NVG ground movement - dangerous'
    },
    BLACKOUT_DRIVE: {
      code: 'BLACKOUT_DRIVE',
      label: 'Blackout Driving',
      icon: ICONS.ORANGE,
      speed_modifier: 0.50,
      accident_risk_increase: 0.25,
      description: 'Blackout driving conditions'
    }
  },
  THREAT_REDUCTION: {
    drone_threat: 0.50,
    manpads_threat: 0.70,
    observation: 0.40,
    description: 'Night reduces visual threat effectiveness'
  }
};

// ============================================================================
// CASEVAC vs MEDEVAC DISTINCTION
// ============================================================================

export const EVACUATION_TYPES = {
  MEDEVAC: {
    code: 'MEDEVAC',
    name: 'Medical Evacuation',
    icon: ICONS.HELICOPTER,
    description: 'Dedicated medical platform with medical personnel',
    protected_status: true,
    en_route_care: 'FULL',
    survival_modifier: 1.0,
    platforms: ['HH-60M', 'IAR-330', 'Ground Ambulance'],
    priority: 'MEDICAL',
    notes: 'Preferred when available and safe'
  },
  CASEVAC: {
    code: 'CASEVAC',
    name: 'Casualty Evacuation',
    icon: ICONS.VEHICLE,
    description: 'Non-medical platform, often combat vehicle',
    protected_status: false,
    en_route_care: 'LIMITED',
    survival_modifier: 0.85,
    platforms: ['Combat Vehicle', 'Utility Helicopter', 'Any Available'],
    priority: 'TACTICAL',
    notes: 'Used when MEDEVAC unavailable or too dangerous'
  },
  SELF_AID: {
    code: 'SELF_AID',
    name: 'Self-Aid/Buddy-Aid Evacuation',
    icon: ICONS.WARNING,
    description: 'Walking wounded self-evacuate or buddy carry',
    protected_status: false,
    en_route_care: 'NONE',
    survival_modifier: 0.70,
    platforms: ['On Foot', 'Any Available'],
    priority: 'SURVIVAL',
    notes: 'Last resort in denied environment'
  }
};

export const EVACUATION_TYPE_BY_PARADIGM = {
  PERMISSIVE: {
    primary: 'MEDEVAC',
    secondary: 'CASEVAC',
    medevac_percentage: 0.85,
    casevac_percentage: 0.12,
    self_aid_percentage: 0.03
  },
  CONTESTED: {
    primary: 'CASEVAC',
    secondary: 'MEDEVAC',
    medevac_percentage: 0.40,
    casevac_percentage: 0.50,
    self_aid_percentage: 0.10
  },
  DENIED: {
    primary: 'CASEVAC',
    secondary: 'SELF_AID',
    medevac_percentage: 0.10,
    casevac_percentage: 0.55,
    self_aid_percentage: 0.35
  }
};

// ============================================================================
// ECHELON-SPECIFIC CONSIDERATIONS
// ============================================================================

export const ECHELON_PARADIGM_EFFECTS = {
  BATTALION: {
    echelon: 'BATTALION',
    icon: ICONS.SHIELD,
    paradigm_sensitivity: 'HIGH',
    permissive_effect: 'Optimal evacuation, Role 1 functions well',
    contested_effect: 'Role 1 stressed, PFC becomes routine',
    denied_effect: 'Role 1 overwhelmed, mass expectant likely',
    golden_hour_achievable: {
      PERMISSIVE: 0.80,
      CONTESTED: 0.30,
      DENIED: 0.05
    }
  },
  BRIGADE: {
    echelon: 'BRIGADE',
    icon: ICONS.SHIELD,
    paradigm_sensitivity: 'MEDIUM',
    permissive_effect: 'Role 2B operates optimally',
    contested_effect: 'Role 2B positioning compromised',
    denied_effect: 'Role 2B may need to displace',
    golden_hour_achievable: {
      PERMISSIVE: 0.75,
      CONTESTED: 0.25,
      DENIED: 0.05
    }
  },
  DIVISION: {
    echelon: 'DIVISION',
    icon: ICONS.SHIELD,
    paradigm_sensitivity: 'LOW',
    permissive_effect: 'Role 2E has good reach',
    contested_effect: 'Role 2E positioning critical',
    denied_effect: 'Role 2E security threatened',
    golden_hour_achievable: {
      PERMISSIVE: 0.70,
      CONTESTED: 0.20,
      DENIED: 0.03
    }
  },
  CORPS: {
    echelon: 'CORPS',
    icon: ICONS.SHIELD,
    paradigm_sensitivity: 'VERY_LOW',
    permissive_effect: 'Role 3 strategic evacuation optimal',
    contested_effect: 'Air evacuation to Role 3 restricted',
    denied_effect: 'Ground only to Role 3',
    golden_hour_achievable: {
      PERMISSIVE: 0.65,
      CONTESTED: 0.15,
      DENIED: 0.02
    }
  }
};

// ============================================================================
// PFC (PROLONGED FIELD CARE) REQUIREMENTS
// ============================================================================

export const PFC_REQUIREMENTS = {
  NO_PFC: {
    code: 'NO_PFC',
    range: '0-60 minutes',
    max_minutes: 60,
    pfc_level: 'NONE',
    icon: ICONS.GREEN,
    description: 'Standard TCCC sufficient',
    equipment_required: 'Basic TCCC kit'
  },
  BASIC_PFC: {
    code: 'BASIC_PFC',
    range: '60-120 minutes',
    max_minutes: 120,
    pfc_level: 'BASIC',
    icon: ICONS.YELLOW,
    description: 'Extended TCCC with monitoring',
    equipment_required: 'TCCC kit + monitoring + IV fluids'
  },
  INTERMEDIATE_PFC: {
    code: 'INTERMEDIATE_PFC',
    range: '120-240 minutes',
    max_minutes: 240,
    pfc_level: 'INTERMEDIATE',
    icon: ICONS.ORANGE,
    description: 'Full PFC with blood products',
    equipment_required: 'PFC kit + blood products + advanced airway'
  },
  ADVANCED_PFC: {
    code: 'ADVANCED_PFC',
    range: '240-480 minutes',
    max_minutes: 480,
    pfc_level: 'ADVANCED',
    icon: ICONS.RED,
    description: 'Advanced PFC with damage control resuscitation',
    equipment_required: 'Full PFC + DCR + possible DCS'
  },
  SUSTAINED_PFC: {
    code: 'SUSTAINED_PFC',
    range: '>480 minutes',
    max_minutes: Infinity,
    pfc_level: 'SUSTAINED',
    icon: ICONS.RED,
    description: 'Sustained operations, forward surgical likely needed',
    equipment_required: 'Forward surgical capability recommended'
  }
};

export const PFC_SURVIVAL_BENEFIT = {
  NO_PFC_AVAILABLE: {
    modifier: 0.70,
    icon: ICONS.RED,
    description: 'No PFC when evacuation >60min - significant mortality increase'
  },
  BASIC_PFC: {
    modifier: 0.90,
    icon: ICONS.YELLOW,
    description: 'Basic PFC - good for 60-120min evacuations'
  },
  INTERMEDIATE_PFC: {
    modifier: 0.95,
    icon: ICONS.GREEN,
    description: 'Intermediate PFC - good for 2-4 hour evacuations'
  },
  ADVANCED_PFC: {
    modifier: 0.98,
    icon: ICONS.GREEN,
    description: 'Advanced PFC - good for 4-8 hour evacuations'
  },
  SUSTAINED_PFC: {
    modifier: 1.0,
    icon: ICONS.GREEN,
    description: 'Sustained PFC capability - optimal extended care'
  }
};

// ============================================================================
// UI HELPER FUNCTIONS - DROPDOWN OPTIONS
// ============================================================================

/**
 * Get evacuation paradigm options for UI dropdown
 * @returns {Array} Array of options with value, label, icon
 */
export const getParadigmOptions = () => {
  return Object.entries(EVACUATION_PARADIGMS).map(([key, paradigm]) => ({
    value: key,
    label: paradigm.label,
    icon: paradigm.icon,
    color: paradigm.color,
    description: paradigm.description,
    goldenHourRate: paradigm.golden_hour_achievable_rate,
    t1Survival: paradigm.t1_survival_rate
  }));
};

/**
 * Get drone threat options for UI dropdown
 * @returns {Array} Array of options with value, label, icon
 */
export const getDroneThreatOptions = () => {
  return Object.entries(DRONE_THREAT_EVACUATION_MODIFIERS).map(([key, threat]) => ({
    value: key,
    label: threat.label,
    icon: threat.icon,
    description: threat.description,
    helicopterModifier: threat.helicopter_modifier,
    groundSpeedModifier: threat.ground_speed_modifier
  }));
};

/**
 * Get weather options for UI dropdown
 * @returns {Array} Array of options with value, label, icon
 */
export const getWeatherOptions = () => {
  return Object.entries(WEATHER_EVACUATION_MODIFIERS).map(([key, weather]) => ({
    value: key,
    label: weather.label,
    icon: weather.icon,
    helicopterAvailability: weather.helicopter_availability,
    droneThreatModifier: weather.drone_threat_modifier,
    visibility: weather.visibility
  }));
};

/**
 * Get AD threat options for UI dropdown
 * @returns {Array} Array of options with value, label, icon
 */
export const getADThreatOptions = () => {
  return Object.entries(AD_THREAT_EVACUATION_MODIFIERS).map(([key, threat]) => ({
    value: key,
    label: threat.label,
    icon: threat.icon,
    description: threat.description,
    helicopterModifier: threat.helicopter_modifier,
    paradigmTendency: threat.paradigm_tendency
  }));
};

/**
 * Get PFC requirement options for UI dropdown
 * @returns {Array} Array of options with value, label, icon
 */
export const getPFCOptions = () => {
  return Object.entries(PFC_REQUIREMENTS).map(([key, pfc]) => ({
    value: key,
    label: pfc.pfc_level,
    icon: pfc.icon,
    description: pfc.description,
    maxMinutes: pfc.max_minutes,
    equipment: pfc.equipment_required
  }));
};

/**
 * Get evacuation type options for UI dropdown
 * @returns {Array} Array of options with value, label, icon
 */
export const getEvacuationTypeOptions = () => {
  return Object.entries(EVACUATION_TYPES).map(([key, type]) => ({
    value: key,
    label: type.name,
    icon: type.icon,
    description: type.description,
    survivalModifier: type.survival_modifier,
    enRouteCare: type.en_route_care
  }));
};

// ============================================================================
// HELPER FUNCTIONS - CALCULATIONS
// ============================================================================

/**
 * Get paradigm by value
 * @param {string} value - Paradigm value
 * @returns {Object} Paradigm object
 */
export const getParadigm = (value) => {
  return EVACUATION_PARADIGMS[value?.toUpperCase()] || CONTESTED;
};

/**
 * Get paradigm icon
 * @param {string} value - Paradigm value
 * @returns {string} Icon
 */
export const getParadigmIcon = (value) => {
  const paradigm = getParadigm(value);
  return paradigm.icon;
};

/**
 * Determine evacuation paradigm based on threat factors
 * @param {Object} factors - Threat factors
 * @returns {Object} Recommended paradigm with reasoning
 */
export const determineEvacuationParadigm = (factors) => {
  const {
    adThreat = 'NONE',
    droneThreat = 'NONE',
    airSuperiority = true,
    groundThreat = 'LOW'
  } = factors;
  
  // Score each factor
  let deniedScore = 0;
  let contestedScore = 0;
  
  // AD threat scoring
  const adScores = {
    NONE: 0, MANPADS_ONLY: 1, SHORAD: 2, MEDIUM_SAM: 3, INTEGRATED_IADS: 4, DENIED: 5
  };
  deniedScore += (adScores[adThreat] || 0) * 2;
  
  // Drone threat scoring
  const droneScores = {
    NONE: 0, LOW: 1, MEDIUM: 2, HIGH: 3, EXTREME: 4
  };
  contestedScore += (droneScores[droneThreat] || 0) * 2;
  
  // Air superiority
  if (!airSuperiority) deniedScore += 3;
  
  // Ground threat
  const groundScores = { LOW: 0, MEDIUM: 1, HIGH: 2, EXTREME: 3 };
  contestedScore += groundScores[groundThreat] || 0;
  
  // Determine paradigm
  let paradigm;
  let reasoning = [];
  
  if (deniedScore >= 6) {
    paradigm = DENIED;
    reasoning.push('High AD/air denial threat');
  } else if (contestedScore >= 4 || deniedScore >= 3) {
    paradigm = CONTESTED;
    reasoning.push('Significant drone/ground threat');
  } else {
    paradigm = PERMISSIVE;
    reasoning.push('Favorable environment');
  }
  
  return {
    paradigm,
    deniedScore,
    contestedScore,
    reasoning,
    factors
  };
};

/**
 * Calculate helicopter availability based on all modifiers
 * @param {Object} paradigm - Evacuation paradigm
 * @param {Object} environment - Environmental factors
 * @returns {Object} Availability calculation
 */
export const calculateHelicopterAvailability = (paradigm, environment = {}) => {
  const {
    weather = 'CLEAR',
    droneThreat = 'NONE',
    adThreat = 'NONE',
    timeOfDay = 'DAY',
    nightEquipment = 'NVG_EQUIPPED'
  } = environment;
  
  let availability = paradigm.helicopter_availability;
  const modifiers = [];
  
  // Weather modifier
  const weatherMod = WEATHER_EVACUATION_MODIFIERS[weather];
  if (weatherMod) {
    availability *= weatherMod.helicopter_availability;
    modifiers.push({ factor: 'Weather', value: weatherMod.helicopter_availability, label: weatherMod.label });
  }
  
  // Drone threat modifier
  const droneMod = DRONE_THREAT_EVACUATION_MODIFIERS[droneThreat];
  if (droneMod) {
    availability *= droneMod.helicopter_modifier;
    modifiers.push({ factor: 'Drone', value: droneMod.helicopter_modifier, label: droneMod.label });
  }
  
  // AD threat modifier
  const adMod = AD_THREAT_EVACUATION_MODIFIERS[adThreat];
  if (adMod) {
    availability *= adMod.helicopter_modifier;
    modifiers.push({ factor: 'AD', value: adMod.helicopter_modifier, label: adMod.label });
  }
  
  // Night operations modifier
  if (timeOfDay === 'NIGHT') {
    const nightMod = NIGHT_OPERATIONS_MODIFIERS.HELICOPTER[nightEquipment];
    if (nightMod) {
      availability *= nightMod.availability_modifier;
      modifiers.push({ factor: 'Night', value: nightMod.availability_modifier, label: nightMod.label });
    }
    
    // Night reduces other threats
    const threatReduction = NIGHT_OPERATIONS_MODIFIERS.THREAT_REDUCTION;
    if (droneThreat !== 'NONE') {
      const nightBonus = 1 + (1 - threatReduction.drone_threat) * (1 - droneMod.helicopter_modifier);
      availability *= nightBonus;
      modifiers.push({ factor: 'Night Drone Reduction', value: nightBonus });
    }
  }
  
  return {
    base_availability: paradigm.helicopter_availability,
    final_availability: Math.max(0, Math.min(1, availability)),
    modifiers,
    environment,
    paradigm: paradigm.value
  };
};

/**
 * Calculate expected evacuation time
 * @param {Object} paradigm - Evacuation paradigm
 * @param {number} distanceKm - Distance to Role 2
 * @param {Object} environment - Environmental factors
 * @returns {Object} Evacuation time calculation
 */
export const calculateEvacuationTime = (paradigm, distanceKm, environment = {}) => {
  const heliAvail = calculateHelicopterAvailability(paradigm, environment);
  
  // Helicopter evacuation time (if available)
  const heliSpeedKph = 200;
  const heliLoadTime = 10;
  const heliTimeMinutes = (distanceKm / heliSpeedKph) * 60 + heliLoadTime;
  
  // Ground evacuation time
  let groundSpeedKph = 40;
  const groundLoadTime = 15;
  
  // Apply ground modifiers
  const weather = WEATHER_EVACUATION_MODIFIERS[environment.weather];
  if (weather) groundSpeedKph *= weather.ground_speed_modifier;
  
  const drone = DRONE_THREAT_EVACUATION_MODIFIERS[environment.droneThreat];
  if (drone) groundSpeedKph *= drone.ground_speed_modifier;
  
  const groundTimeMinutes = (distanceKm / groundSpeedKph) * 60 + groundLoadTime;
  
  // Expected time based on mix
  const heliProbability = heliAvail.final_availability;
  const expectedTime = heliProbability * heliTimeMinutes + (1 - heliProbability) * groundTimeMinutes;
  
  return {
    expected_time_minutes: Math.round(expectedTime),
    helicopter: {
      time_minutes: Math.round(heliTimeMinutes),
      availability: Math.round(heliAvail.final_availability * 100)
    },
    ground: {
      time_minutes: Math.round(groundTimeMinutes),
      speed_kph: Math.round(groundSpeedKph)
    },
    distance_km: distanceKm,
    paradigm: paradigm.value
  };
};

/**
 * Get T1 survival rate by evacuation time
 * @param {number} timeMinutes - Evacuation time in minutes
 * @returns {Object} Survival data
 */
export const getT1SurvivalByTime = (timeMinutes) => {
  for (const [key, data] of Object.entries(T1_SURVIVAL_BY_TIME)) {
    if (timeMinutes <= data.max_minutes) {
      return {
        code: key,
        survival_rate: data.survival_rate,
        range: data.range,
        icon: data.icon,
        description: data.description,
        pfc_required: data.pfc_required || false
      };
    }
  }
  return T1_SURVIVAL_BY_TIME.BEYOND_4H;
};

/**
 * Calculate survival with PFC adjustment
 * @param {number} timeMinutes - Evacuation time
 * @param {string} pfcLevel - PFC capability level
 * @returns {Object} Adjusted survival
 */
export const calculateSurvivalWithPFC = (timeMinutes, pfcLevel = 'NO_PFC_AVAILABLE') => {
  const baseSurvival = getT1SurvivalByTime(timeMinutes);
  const pfcMod = PFC_SURVIVAL_BENEFIT[pfcLevel] || PFC_SURVIVAL_BENEFIT.NO_PFC_AVAILABLE;
  
  // PFC only helps if evacuation > 60 minutes
  if (timeMinutes <= 60) {
    return {
      base_survival: baseSurvival.survival_rate,
      pfc_modifier: 1.0,
      final_survival: baseSurvival.survival_rate,
      pfc_applied: false,
      notes: 'PFC not needed - within golden hour'
    };
  }
  
  const adjustedSurvival = baseSurvival.survival_rate * pfcMod.modifier;
  
  return {
    base_survival: baseSurvival.survival_rate,
    pfc_modifier: pfcMod.modifier,
    final_survival: Math.min(0.92, adjustedSurvival),
    pfc_applied: true,
    pfc_level: pfcLevel,
    icon: pfcMod.icon,
    notes: pfcMod.description
  };
};

/**
 * Determine PFC requirement by time
 * @param {number} timeMinutes - Expected evacuation time
 * @returns {Object} PFC requirement details
 */
export const getPFCRequirement = (timeMinutes) => {
  for (const [key, data] of Object.entries(PFC_REQUIREMENTS)) {
    if (timeMinutes <= data.max_minutes) {
      return {
        code: key,
        level: data.pfc_level,
        range: data.range,
        icon: data.icon,
        description: data.description,
        equipment_required: data.equipment_required
      };
    }
  }
  return { ...PFC_REQUIREMENTS.SUSTAINED_PFC, code: 'SUSTAINED_PFC' };
};

/**
 * Check if golden hour is achievable
 * @param {Object} paradigm - Evacuation paradigm
 * @param {number} distanceKm - Distance to Role 2
 * @param {Object} environment - Environmental factors
 * @returns {Object} Golden hour assessment
 */
export const isGoldenHourAchievable = (paradigm, distanceKm, environment = {}) => {
  const evacTime = calculateEvacuationTime(paradigm, distanceKm, environment);
  const achievable = evacTime.expected_time_minutes <= 60;
  
  return {
    achievable,
    icon: achievable ? ICONS.GREEN : ICONS.RED,
    expected_time: evacTime.expected_time_minutes,
    margin_minutes: 60 - evacTime.expected_time_minutes,
    probability: paradigm.golden_hour_achievable_rate,
    limiting_factors: !achievable ? [
      `Expected time ${evacTime.expected_time_minutes}min exceeds 60min`,
      `Helicopter availability only ${evacTime.helicopter.availability}%`,
      `Ground evacuation time ${evacTime.ground.time_minutes}min`
    ] : []
  };
};

/**
 * Calculate daily MEDEVAC capacity for echelon
 * @param {Object} paradigm - Evacuation paradigm
 * @param {string} echelon - 'BATTALION', 'BRIGADE', 'DIVISION', 'CORPS'
 * @param {number} helicopterCount - Number of MEDEVAC helicopters
 * @param {Object} environment - Environmental factors
 * @returns {Object} Daily capacity calculation
 */
export const calculateDailyMEDEVACCapacity = (paradigm, echelon, helicopterCount, environment = {}) => {
  const echelonData = ECHELON_PARADIGM_EFFECTS[echelon] || ECHELON_PARADIGM_EFFECTS.BRIGADE;
  const heliAvail = calculateHelicopterAvailability(paradigm, environment);
  
  // Base sorties per aircraft per day
  let baseSorties = 8;
  if (paradigm.value === 'CONTESTED') baseSorties = 5;
  if (paradigm.value === 'DENIED') baseSorties = 2;
  
  const patientsPerSortie = 3;
  const effectiveAircraft = helicopterCount * heliAvail.final_availability;
  const dailyCapacity = effectiveAircraft * baseSorties * patientsPerSortie;
  
  return {
    daily_capacity: Math.round(dailyCapacity),
    effective_aircraft: Math.round(effectiveAircraft * 10) / 10,
    sorties_per_aircraft: baseSorties,
    patients_per_sortie: patientsPerSortie,
    helicopter_availability: Math.round(heliAvail.final_availability * 100),
    golden_hour_probability: echelonData.golden_hour_achievable[paradigm.value],
    paradigm: paradigm.value,
    echelon: echelon
  };
};

// ============================================================================
// MONTE CARLO INTEGRATION HELPERS
// ============================================================================

/**
 * Get random evacuation time based on paradigm distribution
 * @param {Object} paradigm - Evacuation paradigm
 * @returns {number} Random evacuation time in minutes
 */
export const getRandomEvacuationTime = (paradigm) => {
  const rand = Math.random();
  const dist = paradigm.evacuation_time_distribution;
  
  let cumulative = 0;
  
  cumulative += dist.under_30min;
  if (rand < cumulative) return 15 + Math.random() * 15;
  
  cumulative += dist['30_to_60min'];
  if (rand < cumulative) return 30 + Math.random() * 30;
  
  cumulative += dist['60_to_120min'];
  if (rand < cumulative) return 60 + Math.random() * 60;
  
  cumulative += dist['120_to_180min'];
  if (rand < cumulative) return 120 + Math.random() * 60;
  
  return 180 + Math.random() * 120;
};

/**
 * Get evacuation paradigm probability distribution
 * @param {string} estimatedParadigm - Best estimate
 * @returns {Object} Probability distribution
 */
export const getParadigmProbabilities = (estimatedParadigm) => {
  switch (estimatedParadigm) {
    case 'PERMISSIVE':
      return { PERMISSIVE: 0.70, CONTESTED: 0.25, DENIED: 0.05 };
    case 'CONTESTED':
      return { PERMISSIVE: 0.15, CONTESTED: 0.65, DENIED: 0.20 };
    case 'DENIED':
      return { PERMISSIVE: 0.05, CONTESTED: 0.25, DENIED: 0.70 };
    default:
      return { PERMISSIVE: 0.33, CONTESTED: 0.34, DENIED: 0.33 };
  }
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate evacuation configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result with errors and warnings
 */
export const validateEvacuationConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  // Validate paradigm
  if (!config.paradigm || !PARADIGM_VALUES.includes(config.paradigm)) {
    errors.push({
      field: 'paradigm',
      message: `Invalid paradigm. Must be one of: ${PARADIGM_VALUES.join(', ')}`,
      icon: ICONS.RED
    });
  }
  
  // Validate drone threat
  if (config.droneThreat && !DRONE_THREAT_EVACUATION_MODIFIERS[config.droneThreat]) {
    errors.push({
      field: 'droneThreat',
      message: 'Invalid drone threat level',
      icon: ICONS.RED
    });
  }
  
  // Validate weather
  if (config.weather && !WEATHER_EVACUATION_MODIFIERS[config.weather]) {
    errors.push({
      field: 'weather',
      message: 'Invalid weather condition',
      icon: ICONS.RED
    });
  }
  
  // Check paradigm-threat consistency
  if (config.paradigm === 'PERMISSIVE' && config.droneThreat === 'EXTREME') {
    warnings.push({
      field: 'consistency',
      message: 'Extreme drone threat inconsistent with permissive paradigm',
      icon: ICONS.WARNING
    });
  }
  
  if (config.paradigm === 'DENIED' && config.droneThreat === 'NONE') {
    warnings.push({
      field: 'consistency',
      message: 'No drone threat unusual for denied paradigm',
      icon: ICONS.WARNING
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if paradigm value is valid
 * @param {string} value - Value to check
 * @returns {boolean} True if valid
 */
export const isValidParadigm = (value) => {
  return PARADIGM_VALUES.includes(value?.toUpperCase());
};

/**
 * Get all valid paradigm values
 * @returns {Array} Valid paradigm values
 */
export const getValidParadigms = () => {
  return [...PARADIGM_VALUES];
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const evacuationParadigmsConstants = {
  // Constants
  T1_SURVIVAL_BY_TIME,
  T2_SURVIVAL_BY_TIME,
  EVACUATION_PARADIGMS,
  PARADIGM_VALUES,
  PERMISSIVE,
  CONTESTED,
  DENIED,
  DRONE_THREAT_EVACUATION_MODIFIERS,
  WEATHER_EVACUATION_MODIFIERS,
  AD_THREAT_EVACUATION_MODIFIERS,
  NIGHT_OPERATIONS_MODIFIERS,
  EVACUATION_TYPES,
  EVACUATION_TYPE_BY_PARADIGM,
  ECHELON_PARADIGM_EFFECTS,
  PFC_REQUIREMENTS,
  PFC_SURVIVAL_BENEFIT,
  
  // UI Options
  getParadigmOptions,
  getDroneThreatOptions,
  getWeatherOptions,
  getADThreatOptions,
  getPFCOptions,
  getEvacuationTypeOptions,
  
  // Helper Functions
  getParadigm,
  getParadigmIcon,
  determineEvacuationParadigm,
  calculateHelicopterAvailability,
  calculateEvacuationTime,
  getT1SurvivalByTime,
  calculateSurvivalWithPFC,
  getPFCRequirement,
  isGoldenHourAchievable,
  calculateDailyMEDEVACCapacity,
  
  // Monte Carlo
  getRandomEvacuationTime,
  getParadigmProbabilities,
  
  // Validation
  validateEvacuationConfig,
  isValidParadigm,
  getValidParadigms
};

export default evacuationParadigmsConstants;
